import type { ReactNode } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { Status } from '@homework/shared';
import { STATUS_LABELS } from '@homework/shared';
import { generatedAvatarDataUri, initialsFor } from '../lib/avatarArt';

/** Small building blocks shared across every screen. */

export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export const STATUS_COLORS: Record<Status, { dot: string; text: string; chip: string; hex: number }> = {
  AVAILABLE: {
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    chip: 'bg-emerald-400/20 text-emerald-800 ring-1 ring-inset ring-emerald-500/25',
    hex: 0x10b981,
  },
  FOCUS: {
    dot: 'bg-violet-500',
    text: 'text-violet-700',
    chip: 'bg-violet-400/20 text-violet-800 ring-1 ring-inset ring-violet-500/25',
    hex: 0x8b5cf6,
  },
  MEETING: {
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    chip: 'bg-amber-400/25 text-amber-800 ring-1 ring-inset ring-amber-500/30',
    hex: 0xf59e0b,
  },
  BREAK: {
    dot: 'bg-cyan-500',
    text: 'text-cyan-700',
    chip: 'bg-cyan-400/20 text-cyan-800 ring-1 ring-inset ring-cyan-500/25',
    hex: 0x06b6d4,
  },
  AWAY: {
    dot: 'bg-slate-400',
    text: 'text-slate-600',
    chip: 'bg-slate-400/15 text-slate-700 ring-1 ring-inset ring-slate-400/25',
    hex: 0x94a3b8,
  },
  OFFLINE: {
    dot: 'bg-slate-300',
    text: 'text-slate-500',
    chip: 'bg-slate-300/15 text-slate-500 ring-1 ring-inset ring-slate-300/30',
    hex: 0xcbd5e1,
  },
};

export function StatusDot({ status, className }: { status: Status; className?: string }) {
  return (
    <span
      className={cx('inline-block h-2.5 w-2.5 shrink-0 rounded-full', STATUS_COLORS[status].dot, className)}
      aria-hidden
    />
  );
}

export function StatusBadge({ status, label }: { status: Status; label?: string }) {
  return (
    <span className={cx('badge', STATUS_COLORS[status].chip)}>
      <StatusDot status={status} />
      {label ?? STATUS_LABELS[status]}
    </span>
  );
}

export function initials(name: string): string {
  return initialsFor(name);
}

const AVATAR_SIZES = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-lg',
  xl: 'h-20 w-20 text-2xl',
} as const;

export function Avatar({
  name,
  photoUrl,
  seed,
  size = 'md',
  status,
  speaking = false,
  className,
}: {
  name: string;
  photoUrl?: string | null;
  /** Stable id so the generated orb survives a rename; falls back to the name. */
  seed?: string;
  size?: keyof typeof AVATAR_SIZES;
  status?: Status;
  /** Draws the halo that says this person is talking right now. */
  speaking?: boolean;
  className?: string;
}) {
  // No photo means the generated 3D orb, not a flat coloured square.
  const source = photoUrl || generatedAvatarDataUri(seed ?? name, name);
  return (
    <span className={cx('relative inline-flex shrink-0', className)}>
      <img
        src={source}
        alt=""
        className={cx(
          'rounded-full object-cover shadow-glass-sm',
          AVATAR_SIZES[size],
          speaking && 'speaking-ring',
        )}
        referrerPolicy="no-referrer"
      />
      {status ? (
        <span
          className={cx(
            'absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-white shadow-sm',
            size === 'xs' || size === 'sm' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5',
            STATUS_COLORS[status].dot,
          )}
          aria-hidden
        />
      ) : null}
    </span>
  );
}

/**
 * Three bars keeping time next to a name, the way a voice memo shows it is
 * recording. Paired with a label for anyone who cannot see it.
 */
export function SpeakingBars({ className }: { className?: string }) {
  return (
    <>
      <span className={cx('speaking-bars', className)} aria-hidden>
        <i />
        <i />
        <i />
      </span>
      <span className="sr-only">is speaking</span>
    </>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </span>
  );
}

export function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center" role="status">
      <Spinner />
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  title = 'Could not load this',
}: {
  error: Error | null;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <div className="card flex flex-col items-start gap-3 p-6" role="alert">
      <div className="flex items-center gap-2 text-rose-600">
        <AlertCircle className="h-5 w-5" aria-hidden />
        <p className="font-semibold">{title}</p>
      </div>
      <p className="text-sm text-slate-600">{error?.message ?? 'Unexpected error.'}</p>
      {onRetry ? (
        <button type="button" className="btn-secondary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
}: {
  icon?: typeof AlertCircle;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-white/80 bg-white/40 p-10 text-center backdrop-blur-sm">
      {Icon ? <Icon className="h-8 w-8 text-slate-400" aria-hidden /> : null}
      <p className="font-semibold text-slate-700">{title}</p>
      {body ? <p className="max-w-md text-sm text-slate-500">{body}</p> : null}
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description ? <p className="mt-1.5 max-w-2xl text-sm text-slate-600">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Card({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('card', className)}>
      {title ? (
        <div className="flex items-start justify-between gap-3 border-b border-white/60 px-5 py-4">
          <div>
            <h2 className="font-semibold text-slate-900">{title}</h2>
            {description ? <p className="mt-0.5 text-sm text-slate-500">{description}</p> : null}
          </div>
          {actions}
        </div>
      ) : null}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'positive' | 'warning';
}) {
  const tones = {
    default: 'text-gradient',
    positive: 'text-emerald-600',
    warning: 'text-amber-600',
  };
  return (
    <div className="card p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={cx('mt-1.5 text-3xl font-bold tabular-nums', tones[tone])}>{value}</p>
      {hint ? <p className="mt-1.5 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}
